// src/services/claudeApi.js
const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';
const CLAUDE_MODEL = 'claude-sonnet-4-6';

const SYSTEM_ARCSEG = `Eres el Cerebro IA de ARCSEG Ingeniería — el asistente técnico institucional de la empresa.

ARCSEG es una firma ecuatoriana de ingeniería especializada en:
- Hidráulica: redes AP/AS, modelación WaterGEMS/SewerGEMS
- Hidrología: cuencas, caudales, HEC-RAS, QGIS
- Hidro-sanitaria: instalaciones internas, BIM MEP en Revit
- SIG: QGIS, Civil 3D, Google My Maps
- BIM: Revit MEP, Navisworks, coordinación de especialidades
- Construcción: residencial, comercial, infraestructura

NORMATIVA VIGENTE ECUADOR:
- Agua potable y saneamiento: Norma INEN, Manual EPMAPS/AMAGUA
- Instalaciones hidro-sanitarias: NEC-HS
- Datos hidrometeorológicos: INAMHI
- Cartografía: IGM 1:25.000, UTM Zona 17 Sur WGS84 (EPSG:32717)
- Datos demográficos: INEC

PARÁMETROS TÉCNICOS ARCSEG:
- Dotación AP residencial urbana: 150-250 L/hab/día
- Presión mínima red: 10 mca | Presión máxima: 50 mca
- Velocidad mínima tuberías: 0.3 m/s | Máxima PVC: 3.0 m/s
- Período retorno alcantarillado urbano: 10-25 años
- Período retorno drenaje pluvial: 5-100 años según riesgo
- Sistema coordenadas: UTM Zona 17 Sur WGS84
- Tc: promedio aritmético Rowe/Kirpich/Chow
- Manning canal principal: n=0.025-0.030

SOFTWARE ESTÁNDAR:
WaterGEMS, SewerGEMS, HEC-RAS, HEC-SSP, QGIS, Civil 3D, Revit MEP, Navisworks, AutoCAD, Excel APU

ENTREGABLES ARCSEG:
- Planos: AutoCAD .dwg + PDF con rotulación ARCSEG
- Modelos BIM: .rvt + .ifc
- SIG: .shp + PDF
- Informes: PDF firmado por Director Técnico

COMPORTAMIENTO:
- Responde en español técnico ecuatoriano
- Usa unidades del Sistema Internacional
- Cuando generes memorias: 1.Antecedentes, 2.Normativa, 3.Parámetros, 4.Desarrollo, 5.Resultados, 6.Conclusiones
- Puedes analizar planos, Excel, PDFs e imágenes que te compartan
- Si ves un archivo adjunto, analízalo y úsalo en tu respuesta
- Siempre indica cuando algo requiere validación del Director Técnico
- No inventes datos que no tengas — señala qué información adicional se necesita`;

// Enviar mensaje con historial completo y archivos adjuntos opcionales
export const enviarMensaje = async (historial, archivosAdjuntos = []) => {
  const apiKey = process.env.REACT_APP_CLAUDE_API_KEY;
  if (!apiKey) throw new Error('API key no configurada. Revisa el archivo .env');

  // Construir mensajes con soporte de archivos
  const messages = historial.map((msg, idx) => {
    // Último mensaje del usuario puede tener archivos
    if (msg.role === 'user' && idx === historial.length - 1 && archivosAdjuntos.length > 0) {
      const content = [];
      // Agregar archivos primero
      archivosAdjuntos.forEach(archivo => {
        if (archivo.tipo === 'image') {
          content.push({
            type: 'image',
            source: { type: 'base64', media_type: archivo.mediaType, data: archivo.data }
          });
        } else if (archivo.tipo === 'document') {
          content.push({
            type: 'document',
            source: { type: 'base64', media_type: archivo.mediaType, data: archivo.data }
          });
        }
      });
      // Agregar texto del mensaje
      if (msg.content) {
        content.push({ type: 'text', text: msg.content });
      }
      return { role: 'user', content };
    }
    return { role: msg.role, content: msg.content };
  });

  const response = await fetch(CLAUDE_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: 4096,
      system: SYSTEM_ARCSEG,
      messages
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Error Claude API: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  return data.content[0].text;
};

// Leer archivo como base64
export const leerArchivoBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Determinar tipo de archivo para la API
export const clasificarArchivo = (file) => {
  const tipo = file.type;
  if (tipo.startsWith('image/')) {
    return { tipo: 'image', mediaType: tipo };
  }
  if (tipo === 'application/pdf') {
    return { tipo: 'document', mediaType: 'application/pdf' };
  }
  // Excel y otros como texto
  return { tipo: 'text', mediaType: tipo };
};

// Apps Script para Sheets/Drive
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby7jd_NvwLlTlpzVXTrv9Lm7hAPUWMUbJW93FrRNoideU8bd_9_uuXpNUkh52b4SHTV/exec';

export const leerProyectosSheets = async () => {
  const response = await fetch(`${APPS_SCRIPT_URL}?accion=listarProyectos`);
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje);
  return data.datos;
};

export const leerEntregablesSheets = async (codigoProyecto) => {
  const response = await fetch(`${APPS_SCRIPT_URL}?accion=listarEntregables&codigo=${codigoProyecto}`);
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje);
  return data.datos;
};

export const guardarEntregableEnSheets = async (datos) => {
  const response = await fetch(APPS_SCRIPT_URL, {
    method: 'POST',
    body: JSON.stringify({ accion: 'guardarEntregable', ...datos })
  });
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje);
  return data.datos;
};
export const leerDealsHubSpot = async () => {
  const response = await fetch(`${APPS_SCRIPT_URL}?accion=listarDealsHubSpot`);
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje);
  return data.datos;
};
export const leerFinanzasSheets = async () => {
  const response = await fetch(`${APPS_SCRIPT_URL}?accion=listarFinanzas`);
  const data = await response.json();
  if (!data.ok) throw new Error(data.mensaje);
  return data.datos;
};